import { Link } from "react-router-dom";
import { AlertTriangle } from "lucide-react";
import { cn } from "@/lib/utils";
import { useSubscription } from "@/hooks/queries/use-billing";

/**
 * A persistent notice shown while an organization's access is degraded
 * (ENG-200-3).
 *
 * Deliberately NOT dismissible. Every write the user attempts will be refused
 * by the API, so a banner they could dismiss would leave them clicking buttons
 * and receiving errors with no visible explanation. The correct way to make it
 * go away is to fix the subscription, and the banner links straight there.
 *
 * Renders nothing at all for an active organization — the epic's second
 * success criterion is that a paying customer sees no change whatsoever, and
 * that includes not reserving layout space for a banner that never appears.
 */
export function EntitlementBanner() {
  const { data } = useSubscription();

  // Nothing while loading, on error, or for an org-less caller. A banner that
  // flashed on every cold load would train users to ignore it.
  if (!data || data.organizationless) {
    return null;
  }

  // A running no-card trial: a gentle reminder in its last few days.
  if (data.access === "full") {
    const daysLeft = data.trialDaysLeft;
    if (daysLeft === null || daysLeft > 3) return null;
    return (
      <div
        role="status"
        data-testid="trial-banner"
        className="mb-4 flex flex-wrap items-center gap-x-3 gap-y-1 rounded-lg border border-primary/30 bg-primary/[0.07] px-4 py-2.5 text-sm"
      >
        <span>
          Your free trial ends in {daysLeft} day{daysLeft === 1 ? "" : "s"}. Add payment details to
          keep working without interruption.
        </span>
        <Link to="/billing" className="font-medium underline underline-offset-4 hover:no-underline">
          Go to billing
        </Link>
      </div>
    );
  }

  const isBillingOnly = data.access === "billing-only";

  return (
    <div
      role="status"
      data-testid="entitlement-banner"
      className={cn(
        "mb-4 flex flex-wrap items-center gap-x-3 gap-y-1 rounded-lg border px-4 py-2.5 text-sm",
        isBillingOnly
          ? "border-red-500/30 bg-red-500/[0.07] text-red-200"
          : "border-amber-500/30 bg-amber-500/[0.07] text-amber-200"
      )}
    >
      <AlertTriangle className="h-4 w-4 shrink-0" />
      <span>
        {isBillingOnly
          ? "This organization does not have an active subscription."
          : data.trialOver
            ? "Your free trial has ended, so your organization is read-only. An admin can add payment details to keep working."
            : "Your organization is read-only — changes cannot be saved."}
      </span>
      <Link
        to="/billing"
        className="font-medium underline underline-offset-4 hover:no-underline"
      >
        {isBillingOnly ? "Activate subscription" : "Review billing"}
      </Link>
    </div>
  );
}
